import type { Task } from "@app/core";
import { AlertCircle, Button, Card, ChevronDown, ChevronsUpDown, Clock, Sun, cn } from "@app/ui";

import { AnimatePresence } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { AgendaRow } from "./AgendaRow.js";

/** Overdue rows shown before "Show all" expands the list. The route holds the
 *  row limit — its keyboard nav must walk exactly the rendered rows. */
export const OVERDUE_COLLAPSED = 8;
/** Rows each "show more" adds. A real backlog can be thousands of overdue items
 *  and "Show all" used to render every one in a single commit. */
export const OVERDUE_PAGE = 100;

export type OverdueSort = "age" | "priority";

interface Props {
  /** Already sorted per the active `sort` (the route owns the ordering — the
   *  keyboard nav needs the same order). */
  items: Task[];
  now: Date;
  sort: OverdueSort;
  onToggleSort: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** How many rows to render (lifted — see OVERDUE_COLLAPSED). */
  limit: number;
  onShowMore: () => void;
  onShowLess: () => void;
  focusedKey: string | null;
  onToggle: (task: Task, done: boolean) => void;
  onEdit: (task: Task) => void;
  onView: (task: Task) => void;
  onSnooze: (task: Task, pos: { x: number; y: number }) => void;
  onMoveToToday: (task: Task, pos: { x: number; y: number }) => void;
  /** Bulk triage: every overdue item at once (one undo step). */
  onBulkMoveToday: () => void;
  onBulkSnooze: () => void;
  bulkBusy?: boolean;
}

/**
 * The Overdue module: header with count, an age↔priority sort toggle, bulk
 * "all to today" / "snooze all until tomorrow" triage buttons (single-undo),
 * and a collapse chevron (persisted by the route). List shows 8 rows before
 * "Show all".
 */
export function OverdueSection({
  items, now, sort, onToggleSort, collapsed, onToggleCollapsed, limit, onShowMore, onShowLess, focusedKey,
  onToggle, onEdit, onView, onSnooze, onMoveToToday, onBulkMoveToday, onBulkSnooze, bulkBusy = false,
}: Props) {
  const { t } = useI18n();
  const shown = items.length <= limit ? items : items.slice(0, limit);
  const more = items.length > shown.length;

  return (
    <section data-today-section="overdue" className="scroll-mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <AlertCircle className="h-4 w-4 text-red-500" aria-hidden /> {t("today.overdue")}
          <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-red-600 dark:text-red-400">
            {items.length}
          </span>
        </h2>
        <div className="ml-auto flex items-center gap-1">
          {!collapsed && (
            <>
              <HeaderAction
                title={sort === "age" ? t("today.sortAge") : t("today.sortPriority")}
                onClick={onToggleSort}
              >
                <ChevronsUpDown className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{sort === "age" ? t("today.sortAge") : t("today.sortPriority")}</span>
              </HeaderAction>
              <HeaderAction title={t("today.bulkMoveAll")} onClick={onBulkMoveToday} disabled={bulkBusy}>
                <Sun className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t("today.bulkMoveAll")}</span>
              </HeaderAction>
              <HeaderAction title={t("today.bulkSnoozeAll")} onClick={onBulkSnooze} disabled={bulkBusy}>
                <Clock className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t("today.bulkSnoozeAll")}</span>
              </HeaderAction>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            title={collapsed ? t("today.expandSection") : t("today.collapseSection")}
            aria-label={collapsed ? t("today.expandSection") : t("today.collapseSection")}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform motion-reduce:transition-none", collapsed && "-rotate-90")} />
          </Button>
        </div>
      </div>
      {!collapsed && (
        <Card className="overflow-hidden border-red-500/20 p-0">
          <ul className="flex flex-col divide-y divide-border text-sm">
            <AnimatePresence initial={false}>
              {shown.map((task) => (
                <AgendaRow
                  key={task.id}
                  task={task}
                  variant="overdue"
                  now={now}
                  navKey={`o:${task.id}`}
                  focusedKey={focusedKey}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onView={onView}
                  onSnooze={onSnooze}
                  onMoveToToday={onMoveToToday}
                />
              ))}
            </AnimatePresence>
          </ul>
          {items.length > OVERDUE_COLLAPSED && (
            <button
              type="button"
              className="w-full border-t px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
              onClick={more ? onShowMore : onShowLess}
            >
              {!more
                ? t("today.showLess")
                : limit > OVERDUE_COLLAPSED
                ? t("list.showing", { shown: String(shown.length), total: String(items.length) })
                : t("today.showAllOverdue", { count: items.length })}
            </button>
          )}
        </Card>
      )}
    </section>
  );
}

function HeaderAction({ title, onClick, disabled = false, children }: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}
